'use client';

import { createContext, createElement, useContext, type ReactNode } from 'react';
import { useBoolVariation, useInitializationStatus } from '@launchdarkly/react-sdk';

/**
 * The Change saturation report's rollout flag (Build Spec v3 §12: "feature flag default OFF";
 * FALCON-247 / OPS-1). Targeted per company, like the integrations flag. Off — or not yet
 * created in LaunchDarkly — hides the Reports card and the report page.
 */
export const CHANGE_SATURATION_FLAG_KEY = 'change-saturation-report';

export type ChangeSaturationFlagState = {
  enabled: boolean;
  /** Flags have not arrived yet: show neither the report nor "not available". */
  isLoading: boolean;
};

export const CHANGE_SATURATION_FLAG_OFF: ChangeSaturationFlagState = {
  enabled: false,
  isLoading: false,
};

export const CHANGE_SATURATION_FLAG_LOADING: ChangeSaturationFlagState = {
  enabled: false,
  isLoading: true,
};

/**
 * Dev/e2e escape hatch, as NEXT_PUBLIC_INTEGRATIONS_ENABLED is for Jira: `true` shows the report
 * without LaunchDarkly. Keep it unset in deployed environments so the flag controls exposure.
 */
export const changeSaturationEnvOverride = (): boolean =>
  process.env.NEXT_PUBLIC_CHANGE_SATURATION_ENABLED === 'true';

const ChangeSaturationFlagContext = createContext<ChangeSaturationFlagState>(
  CHANGE_SATURATION_FLAG_OFF,
);

export function ChangeSaturationFlagProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: ChangeSaturationFlagState;
}) {
  return createElement(ChangeSaturationFlagContext.Provider, { value }, children);
}

/** Read inside the LaunchDarkly provider (the flag bridge), never by pages. */
export function useLaunchDarklyChangeSaturationFlag(): ChangeSaturationFlagState {
  const enabled = useBoolVariation(CHANGE_SATURATION_FLAG_KEY, false);
  const { status } = useInitializationStatus();
  return { enabled, isLoading: status === 'initializing' };
}

/** Whether this company sees the Change saturation report. */
export function useChangeSaturationEnabled(): ChangeSaturationFlagState {
  return useContext(ChangeSaturationFlagContext);
}
